import { produce } from "immer";
import { useEffect, useState } from "react";

import {
  ALLOWED_EXTENSIONS,
  ALLOWED_MIME_TYPES,
} from "features/attachments/constants";

interface UploadedAttachment {
  id: string;
  name: string;
  byteSize: number;
  mimeType: string;
  //in some cases, url has to be fetched from a separate endpoint
  url?: string;
}
interface UploadingAttachment {
  file: File;
  abortController: AbortController;
}
type Attachment =
  | UploadedAttachment
  //attachments that have not started uploading yet
  | { file: File }
  //attachments that are currently uploading
  | UploadingAttachment
  //attachments with errors
  | { file: File; errorMessage: string };

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES_LIMIT = 6;

export default function useAttachments(
  initialState: Attachment[],
  uploadAttachment: (
    file: File,
    abortSignal: AbortSignal,
  ) => Promise<UploadedAttachment>,
) {
  const [attachments, setAttachments] = useState<Attachment[]>(initialState);

  useEffect(() => {
    attachments.forEach((attachment) => {
      if (
        !("abortController" in attachment) &&
        !("errorMessage" in attachment) &&
        !("id" in attachment)
      ) {
        const { file } = attachment;
        const abortController = new AbortController();

        uploadAttachment(file, abortController.signal)
          .then((uploadedAttachment) => {
            setAttachments((prevState) =>
              produce(prevState, (draftAttachments) => {
                for (let i = 0; i < draftAttachments.length; i += 1) {
                  const attachment = draftAttachments[i];
                  if ("file" in attachment && attachment.file === file) {
                    draftAttachments[i] = uploadedAttachment;
                    break;
                  }
                }
              }),
            );
          })
          .catch(() => {
            setAttachments((prevState) =>
              produce(prevState, (draftAttachments) => {
                for (let i = 0; i < draftAttachments.length; i += 1) {
                  const attachment = draftAttachments[i];
                  if ("file" in attachment && attachment.file === file) {
                    draftAttachments[i] = {
                      file,
                      errorMessage: "Error uploading attachment",
                    };
                    break;
                  }
                }
              }),
            );
          });
      }
    });
  }, [attachments]);

  function onUpload(file: File) {
    setAttachments((prevState) => {
      let errorMessage = "";

      if (file.size > MAX_FILE_SIZE) {
        errorMessage = `Larger than ${MAX_FILE_SIZE / 1024 / 1024}MB`;
      }
      if (prevState.length >= MAX_FILES_LIMIT) {
        errorMessage = "File limit exceeded";
      }
      const isAllowedType = ALLOWED_MIME_TYPES.includes(file.type);
      const isAllowedExtension = ALLOWED_EXTENSIONS.some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      );
      if (!isAllowedType && !isAllowedExtension) {
        errorMessage = "File type not supported";
      }

      return [...prevState, errorMessage ? { file, errorMessage } : { file }];
    });
  }

  function onUploadCancel(file: File) {
    const abortedAttachment = attachments.find(
      (attachment) => "file" in attachment && attachment.file === file,
    ) as UploadingAttachment;

    if ("abortController" in abortedAttachment) {
      abortedAttachment.abortController.abort();
    }

    setAttachments(
      attachments.filter((attachment) =>
        "file" in attachment ? attachment.file !== file : true,
      ),
    );
  }

  function removeAttachment(attachmentId: string) {
    setAttachments((prevState) =>
      prevState.filter((attachment) =>
        "id" in attachment ? attachment.id !== attachmentId : true,
      ),
    );
  }

  function resetAttachments(newState?: Attachment[]) {
    attachments.forEach((attachment) => {
      if ("abortController" in attachment) {
        attachment.abortController.abort();
      }
    });
    setAttachments(newState || []);
  }
  return {
    attachments,
    onUpload,
    onUploadCancel,
    removeAttachment,
    resetAttachments,
  };
}
